import { Component, inject, input, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormsModule } from '@angular/forms';
import { CwTextarea } from '@bbraun/cortex-angular';
import { TranslateService } from '@ngx-translate/core';
import { filter, startWith, switchMap, tap } from 'rxjs';
import { TextAreaValueAccessor } from '~/app/directives/TextAreaValueAccessor';

@Component({
  selector: 'gplus-textarea',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss'],
  imports: [CwTextarea, TextAreaValueAccessor, FormsModule]
})
export class TextareaComponent {
  private readonly translateService = inject(TranslateService);

  public control = input<AbstractControl>();
  public label = input<string>();
  public placeholder = input<string>();
  public required = input<boolean>();
  public disabled = input<boolean>();
  public readonly = input<boolean>();
  public attrVef = input<string>();
  public validations = input<Record<string, any>>({});
  public maxLength = input<number>(); // less or equal

  public assistiveText = signal<string>(null);
  public error = signal<string>(null);

  public inputTextValue = toSignal(
    toObservable(this.control).pipe(
      filter((control) => !!control),
      switchMap((control: AbstractControl) =>
        control.valueChanges.pipe(startWith(control.value)).pipe(
          tap((value: string) => {
            if (this.control().pristine) {
              this.error.set('');
            } else {
              this.error.set(
                Object.keys(this.validations()).reduce((err, key) => {
                  return err || (this.control().hasError(key) ? this.validations()[key] : null);
                }, null)
              );
            }

            this.validateInputLength(value);
          })
        )
      )
    ),
    { initialValue: '' }
  );

  public change(value: string) {
    if (!this.control()) {
      return;
    }
    this.control().setValue(value);
    this.control().markAsDirty();
  }

  private validateInputLength(value: string) {
    if (value) {
      if (value.length <= this.maxLength()) {
        // within limit
        this.assistiveText.set(
          `${value.length} / ${this.maxLength()} ${this.translateService.instant('TextareaCharactersUsed')}`
        );
      } else {
        // exceeded limit
        this.error.set(
          `${value.length} / ${this.maxLength()} ${this.translateService.instant('TextareaMaxCharactersError')}`
        );
      }
    } else {
      // empty
      this.assistiveText.set(
        this.translateService.instant('TextareaMaxCharacters', {
          max: this.maxLength()
        })
      );
    }
  }
}
